import { fromJS } from 'immutable';
import { useCallback, useEffect, useRef, useState, useLayoutEffect } from 'react';
import { NavMarker } from 'react-rhumb';
import { useQuery, useMutation } from 'data-fetching-client';
import { useHistory } from 'quartz-routing/react-router';
import { stringify } from 'hub-http/helpers/params';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { AbstractPageTemplate } from 'foundations-components/transitional/components/AbstractPageTemplate';
import NetworkErrorPage from '../components/Utilities/NetworkErrorPage';
import Header from '../components/Header';
import CheckoutContainer from './CheckoutContainer';
import { shouldAutoRedirectToStripeHostedCheckout, redirectToStripeHostedCheckout } from '../utils/stripe';
import { getIframeLayout, LAYOUTS } from '../utils/queryParams';
import { GET_CHECKOUT_SESSION, CREATE_CHECKOUT_SESSION } from '../data/queries';
import useWindowSize from '../hooks/useWindowSize';
import { getIsPaymentPurchaseUrl, getIsIframeableUrl, getIsIframePreviewUrl } from '../utils/urls';
import { useIframeableContext } from '../context/IframeableContext';
import { EMBED_MESSAGE_TYPES } from '../constants/Embed';
import Raven from 'raven-js';
import { FloatingAlertList } from 'foundations-components/transitional/components/FloatingAlertList';
import { PaymentFormContextProvider } from '../context/PaymentFormContext';
import { SubtotalsProvider } from '../context/SubtotalsProvider';
import { useDynamicFavicon } from '../hooks/useDynamicFavicon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getHeight = el => {
  if (!el) {
    return {};
  }
  return el.offsetHeight;
};
export default function ApplicationContainer() {
  var _queryParams$isHealth, _checkoutData$checkou;
  const {
    width
  } = useWindowSize();
  const history = useHistory();
  const ref = useRef(null);
  const iFrameLayout = getIframeLayout();
  const {
    pathname,
    search
  } = history.location;
  const queryParams = Object.fromEntries(new URLSearchParams(search).entries());
  const isHealthCheck = ((_queryParams$isHealth = queryParams.isHealthCheck) === null || _queryParams$isHealth === void 0 ? void 0 : _queryParams$isHealth.toLowerCase()) === 'true';
  const [height, setHeight] = useState(null);
  const isPaymentPurchaseUrl = getIsPaymentPurchaseUrl(pathname);
  const isIframeableUrl = getIsIframeableUrl(pathname);
  const isIframePreviewUrl = getIsIframePreviewUrl(pathname);
  const isEmbed = iFrameLayout === LAYOUTS.EMBED || iFrameLayout === LAYOUTS.EMBED_FULL;
  const hideHeader = isEmbed || iFrameLayout === LAYOUTS.BILLING_PORTAL;
  const {
    iframeableContext
  } = useIframeableContext();
  const [sourceUrl, setSourceUrl] = useState('');
  const getPageMaxWidth = useCallback(() => {
    // Based on GRID_BREAKPOINT_* in HubStyleTokens/sizes
    if (isEmbed) {
      // Width is 100% of page up until 544px. If screen width is >= 544, content
      // width is constrained to 544.
      return 544;
    }

    // Non-embed mode
    switch (true) {
      case width >= 992:
        return 1140;
      case width >= 768:
        return '80%';
      case width >= 544:
        return '90%';
      default:
        return '95%';
    }
  }, [isEmbed, width]);
  const {
    data: checkoutData,
    error: errorFetchCheckoutSession
  } = useQuery(GET_CHECKOUT_SESSION, {
    skip: !(isPaymentPurchaseUrl || isIframePreviewUrl) || isHealthCheck,
    onCompleted: ({
      checkoutSession
    }) => {
      const immutableSession = fromJS(checkoutSession);
      if (shouldAutoRedirectToStripeHostedCheckout(immutableSession)) {
        redirectToStripeHostedCheckout(immutableSession);
      }
    },
    onError: err => {
      const errorResponse = err.networkError && err.networkError.responseJSON;
      if (errorResponse.subCategory === 'CheckoutPayRequestError.SESSION_EXPIRED' && errorResponse.context && errorResponse.context.sourceUrl) {
        setSourceUrl(errorResponse.context.sourceUrl[0]);
      }
    }
  });
  const branding = checkoutData === null || checkoutData === void 0 || (_checkoutData$checkou = checkoutData.checkoutSession) === null || _checkoutData$checkou === void 0 ? void 0 : _checkoutData$checkou.userDefinedBranding;
  useDynamicFavicon({
    faviconUrl: branding === null || branding === void 0 ? void 0 : branding.faviconUrl
  });
  const [createCheckoutSession, {
    error: errorCreateCheckoutSession
  }] = useMutation(CREATE_CHECKOUT_SESSION, {
    update: (cache, {
      data: checkoutSessionData
    }) => {
      cache.writeQuery({
        query: GET_CHECKOUT_SESSION,
        data: checkoutSessionData
      });
    },
    onCompleted: ({
      checkoutSession
    }) => {
      if (!isPaymentPurchaseUrl) {
        const paramString = stringify(queryParams);
        let updatedPathname = `/purchase/${checkoutSession.id}`;
        if (isIframeableUrl && !!iframeableContext) {
          updatedPathname = `/iframe${updatedPathname}`;
          iframeableContext.sendMessage('SESSION_ID', {
            data: checkoutSession.id
          });
        }
        history.replace({
          pathname: updatedPathname,
          search: paramString
        });
      }
      const immutableSession = fromJS(checkoutSession);
      if (shouldAutoRedirectToStripeHostedCheckout(immutableSession)) {
        redirectToStripeHostedCheckout(immutableSession);
      }
    },
    onError: () => {
      // Errors are already handled/reported in CheckoutSessionApi - if we don't catch them here
      // then Apollo rethrows a duplicate ApolloError
    }
  });
  useEffect(() => {
    if (isHealthCheck) {
      return;
    }
    if (!isPaymentPurchaseUrl && !isIframePreviewUrl) {
      createCheckoutSession().catch(err => Raven.captureException(err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useLayoutEffect(() => {
    const newHeight = getHeight(ref.current);
    if (typeof newHeight === 'number' && height !== null && ref && ref.current && iFrameLayout === LAYOUTS.FULL_PAGE_OVERLAY && (!height || height + 25 !== newHeight)) {
      setHeight(newHeight);
      window.parent.postMessage({
        height: height + 25
      }, '*');
    }
  }, [iFrameLayout, height]);
  const error = errorCreateCheckoutSession || errorFetchCheckoutSession;
  const statusCode = error && error.networkError && 'status' in error.networkError && error.networkError.status;
  if (error && typeof statusCode === 'number') {
    if (isIframeableUrl && !!iframeableContext) {
      iframeableContext.sendMessage(EMBED_MESSAGE_TYPES.FAILED);
    }
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "NOT_FOUND",
      children: /*#__PURE__*/_jsx(NetworkErrorPage, {
        statusCode: statusCode,
        sourceUrl: sourceUrl
      })
    });
  }

  // Renders a lightweight response when isHealthCheck=true query parameter
  // is present. This allows proxy mapping ATs to succeed without requiring
  // a specific checkout session ID or session template ID.
  if (isHealthCheck) {
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "HEALTH_CHECK",
      children: /*#__PURE__*/_jsx("div", {
        "data-test-id": "health-check-response",
        children: "OK"
      })
    });
  }
  const immutableSession = checkoutData && fromJS(checkoutData.checkoutSession);
  const missingCheckoutDataOrNeedsRedirect = !checkoutData || shouldAutoRedirectToStripeHostedCheckout(immutableSession);
  if (missingCheckoutDataOrNeedsRedirect) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      "data-test-id": "app-loading-spinner",
      grow: true,
      minHeight: 200
    });
  }
  return /*#__PURE__*/_jsx(PaymentFormContextProvider, {
    checkoutSession: immutableSession,
    children: /*#__PURE__*/_jsx(SubtotalsProvider, {
      checkoutSession: immutableSession,
      children: /*#__PURE__*/_jsxs(NavMarker, {
        name: "INDEX_LOADED",
        children: [/*#__PURE__*/_jsx(FloatingAlertList, {}), /*#__PURE__*/_jsx("div", {
          id: "payments-ui-app-page-container",
          style: {
            position: 'relative',
            minHeight: '100vh'
          },
          ref: ref,
          children: /*#__PURE__*/_jsx(AbstractPageTemplate, {
            "data-test-id": "page-template",
            headerComponent: !hideHeader && (!isIframeableUrl || isIframePreviewUrl) && branding && /*#__PURE__*/_jsx(Header, {
              branding: branding
            }),
            horizontalDivider: !hideHeader
            // the auto generated types for the "pageMaxWidth" prop do not account for valid string values such as '75%
            ,
            pageMaxWidth: getPageMaxWidth(),
            children: /*#__PURE__*/_jsx(CheckoutContainer, {
              checkoutSession: immutableSession,
              trackPageView: !isIframePreviewUrl
            })
          })
        })]
      })
    })
  });
}